import React, { useEffect, useState } from 'react';
import Drawer from '../common/Drawer';
import Button from '../common/Button';
import DateField from '../common/formElements/DateField';
import { NetcashBatchDTO } from '../../services/apiClient';
import {
  formatActionDate,
  getDefaultNetcashActionDate,
  getEarliestNetcashActionDate,
  validateNetcashActionDate,
} from '../../utils/netcashDates';

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

interface NetcashPushDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  batch: NetcashBatchDTO | null;
  isSubmitting: boolean;
  onConfirm: (actionDate: string) => void;
}

const NetcashPushDrawer: React.FC<NetcashPushDrawerProps> = ({ isOpen, onClose, batch, isSubmitting, onConfirm }) => {
  const [actionDate, setActionDate] = useState('');

  useEffect(() => {
    if (isOpen && batch) {
      setActionDate(getDefaultNetcashActionDate(batch.year ?? new Date().getFullYear(), batch.month ?? new Date().getMonth() + 1));
    }
  }, [isOpen, batch?.id]);

  if (!batch) return null;

  const error = actionDate ? validateNetcashActionDate(actionDate) : 'Please choose a collection date.';

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Push to Netcash" size="small">
      <div className="space-y-5">
        <div className="rounded-lg border border-secondary bg-primary/5 p-4">
          <div className="text-sm font-semibold text-primary dark:text-gray-200">
            {monthNames[(batch.month ?? 1) - 1]} {batch.year} batch
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {batch.totalCount} invoice(s) | R{(batch.totalAmount ?? 0).toFixed(2)}
          </div>
        </div>

        <div>
          <label htmlFor="netcash-action-date" className="block text-sm font-medium text-primary dark:text-gray-200 mb-1">
            Collection date
          </label>
          <DateField
            id="netcash-action-date"
            value={actionDate}
            minDate={getEarliestNetcashActionDate()}
            onChange={(e) => setActionDate(e.target.value)}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Business days only (Monday to Friday), at least 2 business days from today - TwoDay debit orders can't be dated tomorrow. Public
            holidays aren't checked, so avoid them.
          </p>
          {error ? (
            <p className="text-sm text-red-500 mt-2">{error}</p>
          ) : (
            <p className="text-sm text-green-700 dark:text-green-400 mt-2">Members will be debited on {formatActionDate(actionDate)}.</p>
          )}
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400">
          You will still need to authorise/release the batch in the Netcash portal after it is uploaded.
        </p>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => onConfirm(actionDate)}
            isLoading={isSubmitting}
            disabled={isSubmitting || !!error}
          >
            Push to Netcash
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default NetcashPushDrawer;
